/**
 * Canonical payload builder for POST /api/admin/wishlist-config (entries[]).
 * Shared with WishlistAdmin save() — keep in sync with api/admin/wishlist-config.ts validation.
 */

import {
  AR_CITY_CATALOG,
  AR_PROVINCES,
  INTERNATIONAL_COUNTRIES,
  type GeoCtaType,
} from "./wishlistGeoCatalog";

export type GeoScope = "province_ar" | "country" | "city_ar";

export interface WishlistGeoPersistEntry {
  scope: GeoScope;
  geo_code: string;
  cta_type: GeoCtaType;
  label?: string | null;
  url?: string | null;
  enabled: boolean;
}

const DEFAULT_AR_CTA: GeoCtaType = "none";
const DEFAULT_COUNTRY_CTA: GeoCtaType = "none";

export interface CityRowInput {
  key: string;
  geo_code: string;
  cta_type: GeoCtaType;
}

/**
 * Fuerza cada ciudad del catálogo al CTA de su provincia.
 * Uso: botón explícito "Alinear catálogo" o bulk "todas las provincias".
 * NO llamar en cada save — borraría overrides de barrio/ciudad.
 */
export function alignCityCatalogToProvinces(
  arConfig: Record<string, GeoCtaType>,
  cityCatalogConfig?: Record<string, GeoCtaType>,
): Record<string, GeoCtaType> {
  const next = cityCatalogConfig ? { ...cityCatalogConfig } : {};
  for (const c of AR_CITY_CATALOG) {
    next[c.geo_code] = arConfig[c.provinceCode] ?? DEFAULT_AR_CTA;
  }
  return next;
}

/**
 * Hidrata el catálogo desde filas DB `city_ar` (geo_code normalizado → cta).
 * Preferencia: cta ≠ none de canónico o alias; si solo hay none, usa none;
 * si no hay fila, hereda provincia.
 */
export function hydrateCityCatalogFromDb(
  arConfig: Record<string, GeoCtaType>,
  dbCityByNormKey: Map<string, GeoCtaType>,
  normalizeKey: (s: string) => string,
): Record<string, GeoCtaType> {
  const catalog: Record<string, GeoCtaType> = {};
  for (const c of AR_CITY_CATALOG) {
    const keys = [c.geo_code, ...(c.aliases ?? []).map((a) => a.geo_code)];
    let found: GeoCtaType | undefined;
    for (const k of keys) {
      const t = dbCityByNormKey.get(normalizeKey(k));
      if (t === undefined) continue;
      if (t !== "none") {
        found = t;
        break;
      }
      if (found === undefined) found = t;
    }
    catalog[c.geo_code] =
      found !== undefined ? found : (arConfig[c.provinceCode] ?? DEFAULT_AR_CTA);
  }
  return catalog;
}

/** Bulk-set CTA for all catalog cities whose `region` matches (e.g. "Barrios CABA"). */
export function setCityCatalogRegionCta(
  cityCatalogConfig: Record<string, GeoCtaType>,
  region: string,
  cta: GeoCtaType,
): Record<string, GeoCtaType> {
  const next = { ...cityCatalogConfig };
  for (const c of AR_CITY_CATALOG) {
    if (c.region === region) next[c.geo_code] = cta;
  }
  return next;
}

/**
 * Bulk-set CTA for every catalog city under a province (e.g. CABA = capital + barrios).
 * Used so "CABA → Exclusivo" also flips cf.city keys (caba, buenos aires, palermo…).
 */
export function setCityCatalogProvinceCta(
  cityCatalogConfig: Record<string, GeoCtaType>,
  provinceCode: string,
  cta: GeoCtaType,
): Record<string, GeoCtaType> {
  const next = { ...cityCatalogConfig };
  for (const c of AR_CITY_CATALOG) {
    if (c.provinceCode === provinceCode) next[c.geo_code] = cta;
  }
  return next;
}

/**
 * Cuando el admin cambia el CTA de una provincia, alinear el catálogo city_ar
 * de esa provincia. Si no, city_ar=none en DB gana al Worker y el botón Exclusivo
 * no aparece aunque province_ar=community (caso clásico CABA / capital CF).
 */
export function cascadeProvinceCtaToCityCatalog(
  cityCatalogConfig: Record<string, GeoCtaType>,
  provinceCode: string,
  cta: GeoCtaType,
): Record<string, GeoCtaType> {
  return setCityCatalogProvinceCta(cityCatalogConfig, provinceCode, cta);
}

export function countCityCatalogProvinceMismatches(
  arConfig: Record<string, GeoCtaType>,
  cityCatalogConfig: Record<string, GeoCtaType>,
): number {
  let n = 0;
  for (const c of AR_CITY_CATALOG) {
    const cityCta = cityCatalogConfig[c.geo_code] ?? DEFAULT_AR_CTA;
    const provCta = arConfig[c.provinceCode] ?? DEFAULT_AR_CTA;
    if (cityCta !== provCta) n += 1;
  }
  return n;
}

export function buildWishlistGeoPersistEntries(input: {
  arConfig: Record<string, GeoCtaType>;
  countryConfig: Record<string, GeoCtaType>;
  cityCatalogConfig: Record<string, GeoCtaType>;
  cityArRows: CityRowInput[];
}): WishlistGeoPersistEntry[] {
  const { arConfig, countryConfig, cityCatalogConfig, cityArRows } = input;

  return [
    ...AR_PROVINCES.map((p) => ({
      scope: "province_ar" as const,
      geo_code: p.code,
      cta_type: arConfig[p.code] ?? DEFAULT_AR_CTA,
      enabled: true,
    })),
    ...INTERNATIONAL_COUNTRIES.map((c) => ({
      scope: "country" as const,
      geo_code: c.code,
      cta_type: countryConfig[c.code] ?? DEFAULT_COUNTRY_CTA,
      enabled: true,
    })),
    ...AR_CITY_CATALOG.flatMap((c) => {
      const ctaType: GeoCtaType = cityCatalogConfig[c.geo_code] ?? "none";
      const canonical: WishlistGeoPersistEntry = {
        scope: "city_ar",
        geo_code: c.geo_code,
        cta_type: ctaType,
        label: c.name,
        enabled: true,
      };
      const aliasEntries: WishlistGeoPersistEntry[] = (c.aliases ?? []).map((a) => ({
        scope: "city_ar",
        geo_code: a.geo_code,
        cta_type: ctaType,
        label: `${c.name} — ${a.name}`,
        enabled: true,
      }));
      return [canonical, ...aliasEntries];
    }),
    ...cityArRows
      .filter((row) => row.geo_code.trim())
      .map((row) => ({
        scope: "city_ar" as const,
        geo_code: row.geo_code.trim(),
        cta_type: row.cta_type,
        enabled: true,
      })),
  ];
}

/** Snapshot comparable post-load / post-save. */
export type WishlistGeoBaseline = {
  ar: Record<string, GeoCtaType>;
  country: Record<string, GeoCtaType>;
  cityCatalog: Record<string, GeoCtaType>;
  /** normKey → cta (ad-hoc city_ar fuera del catálogo) */
  adhoc: Record<string, GeoCtaType>;
  hideGlobalHeroCta: boolean;
};

export function snapshotWishlistBaseline(input: {
  arConfig: Record<string, GeoCtaType>;
  countryConfig: Record<string, GeoCtaType>;
  cityCatalogConfig: Record<string, GeoCtaType>;
  cityArRows: CityRowInput[];
  hideGlobalHeroCta: boolean;
  normalizeKey: (s: string) => string;
}): WishlistGeoBaseline {
  const adhoc: Record<string, GeoCtaType> = {};
  for (const r of input.cityArRows) {
    const g = r.geo_code.trim();
    if (!g) continue;
    adhoc[input.normalizeKey(g)] = r.cta_type;
  }
  return {
    ar: { ...input.arConfig },
    country: { ...input.countryConfig },
    cityCatalog: { ...input.cityCatalogConfig },
    adhoc,
    hideGlobalHeroCta: input.hideGlobalHeroCta,
  };
}

export type WishlistDirtyDiff = {
  entries: WishlistGeoPersistEntry[];
  /** city_ar geo_codes a borrar (ad-hoc quitados) */
  deleteCityAr: string[];
  /** Resumen legible para el admin */
  summary: {
    provinces: string[];
    countries: string[];
    cities: string[];
    deletedCities: string[];
    hideGlobalChanged: boolean;
  };
  total: number;
};

/**
 * Solo filas que difieren del baseline — save selectivo de impacto real.
 * Incluye aliases del catálogo cuando cambia el canónico (Worker matchea cada geo_code).
 */
export function buildWishlistGeoDirtyDiff(input: {
  baseline: WishlistGeoBaseline | null;
  arConfig: Record<string, GeoCtaType>;
  countryConfig: Record<string, GeoCtaType>;
  cityCatalogConfig: Record<string, GeoCtaType>;
  cityArRows: CityRowInput[];
  hideGlobalHeroCta: boolean;
  normalizeKey: (s: string) => string;
}): WishlistDirtyDiff {
  const entries: WishlistGeoPersistEntry[] = [];
  const summary = {
    provinces: [] as string[],
    countries: [] as string[],
    cities: [] as string[],
    deletedCities: [] as string[],
    hideGlobalChanged: false,
  };

  const base = input.baseline;
  if (!base) {
    // Sin baseline (primer paint): full persist seguro
    const full = buildWishlistGeoPersistEntries(input);
    return {
      entries: full,
      deleteCityAr: [],
      summary: {
        provinces: AR_PROVINCES.map((p) => p.code),
        countries: INTERNATIONAL_COUNTRIES.map((c) => c.code),
        cities: full.filter((e) => e.scope === "city_ar").map((e) => e.geo_code),
        deletedCities: [],
        hideGlobalChanged: true,
      },
      total: full.length,
    };
  }

  const ctaOr = (v: GeoCtaType | undefined, d: GeoCtaType) => v ?? d;

  for (const p of AR_PROVINCES) {
    const next = ctaOr(input.arConfig[p.code], DEFAULT_AR_CTA);
    const prev = ctaOr(base.ar[p.code], DEFAULT_AR_CTA);
    if (next !== prev) {
      entries.push({
        scope: "province_ar",
        geo_code: p.code,
        cta_type: next,
        enabled: true,
      });
      summary.provinces.push(p.code);
    }
  }

  for (const c of INTERNATIONAL_COUNTRIES) {
    const next = ctaOr(input.countryConfig[c.code], DEFAULT_COUNTRY_CTA);
    const prev = ctaOr(base.country[c.code], DEFAULT_COUNTRY_CTA);
    if (next !== prev) {
      entries.push({
        scope: "country",
        geo_code: c.code,
        cta_type: next,
        enabled: true,
      });
      summary.countries.push(c.code);
    }
  }

  for (const c of AR_CITY_CATALOG) {
    const next = ctaOr(input.cityCatalogConfig[c.geo_code], DEFAULT_AR_CTA);
    const prev = ctaOr(base.cityCatalog[c.geo_code], DEFAULT_AR_CTA);
    if (next === prev) continue;
    entries.push({
      scope: "city_ar",
      geo_code: c.geo_code,
      cta_type: next,
      label: c.name,
      enabled: true,
    });
    summary.cities.push(c.geo_code);
    for (const a of c.aliases ?? []) {
      entries.push({
        scope: "city_ar",
        geo_code: a.geo_code,
        cta_type: next,
        label: `${c.name} — ${a.name}`,
        enabled: true,
      });
    }
  }

  const currentAdhoc = new Map<string, { geo: string; cta: GeoCtaType }>();
  for (const r of input.cityArRows) {
    const g = r.geo_code.trim();
    if (!g) continue;
    currentAdhoc.set(input.normalizeKey(g), { geo: g, cta: r.cta_type });
  }

  for (const [norm, { geo, cta }] of currentAdhoc) {
    const prev = base.adhoc[norm];
    if (prev === undefined || prev !== cta) {
      entries.push({
        scope: "city_ar",
        geo_code: geo,
        cta_type: cta,
        enabled: true,
      });
      if (!summary.cities.includes(geo)) summary.cities.push(geo);
    }
  }

  const deleteCityAr: string[] = [];
  for (const [norm, prevCta] of Object.entries(base.adhoc)) {
    if (currentAdhoc.has(norm)) continue;
    // geo_code guardado como norm en load ad-hoc — borrar por esa key
    deleteCityAr.push(norm);
    summary.deletedCities.push(norm);
    void prevCta;
  }

  summary.hideGlobalChanged = input.hideGlobalHeroCta !== base.hideGlobalHeroCta;

  return {
    entries,
    deleteCityAr,
    summary,
    total: entries.length + deleteCityAr.length + (summary.hideGlobalChanged ? 1 : 0),
  };
}
